import type { MouseEvent, MouseEventHandler } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getColorWithAlpha } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import Skeleton from 'react-loading-skeleton';
import { Range } from 'react-range';
import { getAnimation, timeFormat } from '../../../helpers';
import { useTheme } from '../../common';
import { usePlayer } from '../PlayerProvider';

interface ProgressBarProps {
  className?: string;
  hideTime?: boolean;
}

export function ProgressBar({ className, hideTime }: ProgressBarProps) {
  const componentRef = useRef<HTMLDivElement>(null);
  const { textColor } = useTheme();
  const { accentColor, sourceType } = useSettings();
  const {
    progress: { played = 0, loaded = 0 },
    seekTo,
    currentItem,
    duration,
    loading
  } = usePlayer();

  const [seekerPosition, setSeekerPosition] = useState<number | number[]>(0);
  const [inChange, setInChange] = useState(false);
  const [fullTimeInSeconds, setFullTimeInSeconds] = useState(0);
  const [hoverTime, setHoverTime] = useState(0);

  useEffect(() => {
    if (currentItem) setFullTimeInSeconds(duration);
  }, [currentItem, duration]);

  const onMouseMove: MouseEventHandler = (event: MouseEvent) => {
    if (!componentRef.current) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();

    setHoverTime(
      ((event.clientX - rect.left) / componentRef.current.clientWidth) * 100
    );
  };

  useEffect(() => {
    if (!inChange) {
      setSeekerPosition(Number((played * 100).toFixed(1)));
    }
  }, [played]);

  return (
    sourceType !== 'radio' && (
      <Component
        className={className}
        onMouseMove={onMouseMove}
        ref={componentRef}
      >
        <Range
          step={0.1}
          min={0}
          max={100}
          values={
            Array.isArray(seekerPosition) ? seekerPosition : [seekerPosition]
          }
          onChange={(values) => {
            setInChange(true);
            setSeekerPosition(values);
          }}
          onFinalChange={(values) => {
            setSeekerPosition(values);
            seekTo(values[0] / 100);
            setInChange(false);
          }}
          renderTrack={({ props, children }) => (
            <Track {...props} style={{ ...props.style }}>
              {children}

              {!hideTime && (
                <Time>
                  <TimeStart>
                    {!loading ? (
                      timeFormat(
                        Number((fullTimeInSeconds * played).toFixed(0))
                      )
                    ) : (
                      <Skeleton duration={0.8} width="28px" />
                    )}
                  </TimeStart>

                  <TimeEnd>
                    {!loading ? (
                      timeFormat(fullTimeInSeconds)
                    ) : (
                      <Skeleton duration={0.8} width="28px" />
                    )}
                  </TimeEnd>
                </Time>
              )}

              <FakeTrack _color={textColor} />
              <ProgressBarLoaded
                style={{ width: `${loaded * 100}%` }}
                _color={textColor}
              />
              <ProgressBarPlayed
                style={{ width: `${seekerPosition}%` }}
                _color={accentColor}
              />
              <HoverTime style={{ left: `${hoverTime}%` }}>
                {timeFormat(Number(((duration * hoverTime) / 100).toFixed(0)))}
              </HoverTime>
            </Track>
          )}
          renderThumb={({ props: { key, ...props } }) => (
            <Thumb key={key} _backgroundColor={accentColor} {...props} />
          )}
        />
      </Component>
    )
  );
}

export const HoverTime = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  transform: translate(-50%, -100%);
  pointer-events: none;
  opacity: 0;
  border-radius: 4px;
  box-shadow: 0 5px 8px 0 rgba(17, 17, 17, 0.2);
  background-color: #282828;
  color: #fff;
  margin-top: 8px;
  padding: 2px 9px 3px;
  font-size: 11px;
  text-align: center;
  z-index: 10;
  transition: ${getAnimation('opacity')};
`;

export const Thumb = styled.div<{
  _backgroundColor: string;
}>`
  display: block !important;
  height: 12px;
  width: 12px;
  border-radius: 50%;
  opacity: 0;
  pointer-events: none;
  top: 11px;
  z-index: 12 !important;
  transition: 0.2s ease opacity;
  background-color: ${({ _backgroundColor }) => _backgroundColor};
`;

const Component = styled.div`
  position: relative;
  width: 100%;

  &:hover ${HoverTime}, &:hover ${Thumb} {
    opacity: 1;
  }
`;

export const ProgressBarPlayed = styled.div<{
  _color: string;
}>`
  display: block !important;
  position: absolute;
  left: 0;
  z-index: 3;
  pointer-events: none;
  border-radius: 4px;
  height: 4px;
  transition: 0.1s ease all;
  bottom: 6px;
  background-color: ${({ _color }) => _color};
`;

export const ProgressBarLoaded = styled.div<{
  _color: string;
}>`
  display: block !important;
  position: absolute;
  height: 4px;
  left: 0;
  z-index: 2;
  pointer-events: none;
  border-radius: 4px;
  transition: 0.1s ease all;
  bottom: 6px;
  background-color: ${({ _color }) => getColorWithAlpha(_color, 0.2)};
`;

const TimeStart = styled.div`
  position: absolute;
  left: 0;
  pointer-events: none;
`;

const TimeEnd = styled.div`
  position: absolute;
  right: 0;
  pointer-events: none;
`;

export const FakeTrack = styled.div<{
  _color: string;
}>`
  display: block !important;
  width: 100%;
  position: absolute;
  left: 0;
  z-index: 3;
  pointer-events: none;
  border-radius: 4px;
  height: 4px;
  bottom: 6px;
  background-color: ${({ _color }) => getColorWithAlpha(_color, 0.2)};
`;

export const Time = styled.div`
  transition: ${getAnimation()};
  pointer-events: none;
  width: 100%;
  height: 12px;
  line-height: 12px;
  z-index: 5;
  font-size: 11px;
  opacity: 0.7;
  position: relative;
  top: -4px;
`;

export const Track = styled.div`
  height: 26px;
  padding: 6px 0;
  width: 100%;
  border-radius: 2px;
  margin-top: -4px;
  box-sizing: border-box;
`;
